import store, { RootState } from "../store";
import { TAX_PAYER_SUSPENDED } from "../models/tax-payer";
import { markTaxPayerSuspended } from "../redux/tax-payer/tax-payer-slice";

/**
 * A company can be suspended while someone is using it. The server then
 * refuses every request with a 403 that says so, and the app switches to the
 * suspension screen instead of showing scattered errors.
 */
const detectSuspension = async (response: Response, taxPayerId?: number) => {
  if (response.status !== 403 || taxPayerId === undefined) {
    return;
  }

  try {
    const body = await response.clone().json();
    if (body?.status === TAX_PAYER_SUSPENDED && body.suspension) {
      store.dispatch(
        markTaxPayerSuspended({ taxPayerId, suspension: body.suspension }),
      );
    }
  } catch {
    // Not JSON: nothing to learn from it.
  }
};

const generateCorrelationId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
};

export const fetchAuthenticated = async (
  input: RequestInfo,
  init: RequestInit,
): Promise<Response> => {
  const state: RootState = store.getState();
  const taxPayerId = state.taxPayer.data.taxPayer.id;
  const accessToken = state.user.accessToken;
  const correlationId = generateCorrelationId();
  if (init.headers === undefined) {
    init.headers = {
      Authorization: `Bearer ${accessToken}`,
      "X-Correlation-ID": correlationId,
    };
    if (taxPayerId !== undefined) {
      (init.headers as Record<string, string>)["X-Tax-Payer-ID"] =
        taxPayerId.toString();
    }
  } else {
    let headers = init.headers as Record<string, string>;
    headers.Authorization = `Bearer ${accessToken}`;
    headers["X-Correlation-ID"] = correlationId;
    if (taxPayerId !== undefined) {
      headers["X-Tax-Payer-ID"] = taxPayerId.toString();
    }
    init.headers = headers;
  }
  const response = await fetch(input, init);
  await detectSuspension(response, taxPayerId);
  return response;
};
